import { CONVERTIBLE_IMAGE_EXTENSIONS } from '~/modules/file';

/**
 * The extensions supported by the image service.
 */
const IMAGE_SERVICE_EXTENSIONS = [...CONVERTIBLE_IMAGE_EXTENSIONS];

/**
 * Check if a file is supported by the image service.
 * @param filename The filename to check.
 * @returns The file is supported by the image service.
 */
function isSupportedImageService(filename: string) {
    const extension = filename.split('.').pop()?.toLowerCase();
    if (!extension) {
        return false;
    }
    return IMAGE_SERVICE_EXTENSIONS.includes(extension);
}

/**
 * Get the image service URL from a S3 website file URL. If the URL is not an
 * S3 website file URL or is not a supported image file type, return the original URL.
 * @param url The S3 website file URL.
 * @returns The image service URL or the original URL if not supported.
 * @see {@link app/src/Lib/Image/ImageAPIBuilder.php}
 */
function getImageServiceURL(url: string) {
    if (url.startsWith('https://') || url.startsWith('http://')) {
        return url;
    }
    // S3 website file URLs start with /assets/.
    if (!url.startsWith('/assets/')) {
        return url;
    }
    const filename = url.split('/').pop();
    if (!filename || !isSupportedImageService(filename)) {
        return url;
    }
    // Formula: Replace /assets/ with /images/s1/,
    // e.g. /assets/ConversationThumbnails/image.png -> /images/s1/ConversationThumbnails/image.png
    const path = url.split('/assets/').pop() ?? '';
    return `/images/s1/${path}`;
}

/**
 * Get the URL for a resized user-uploaded image, using the image service.
 * Returns the original URL if the file is not an S3 website file URL, or is not
 * an image supported by the image service.
 * @param url The URL of the user-uploaded image.
 * @param width The resized image width.
 * @param height The resized image height.
 * @returns The URL for the resized image.
 */
export function getResizedImageURL(
    url: string,
    width: number,
    height?: number,
) {
    const imageServiceURL = getImageServiceURL(url);
    if (imageServiceURL === url) {
        return url;
    }
    const params = {
        edits: [
            {
                type: 'resize',
                options: { width, height, fit: 'inside' },
            },
        ],
        quality: 92,
        // Support transparent backgrounds.
        transparent: true,
    };
    const base64Params = btoa(JSON.stringify(params));
    return `${imageServiceURL}/${base64Params}`;
}
